// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Role Store (Zustand)
// Manages RBAC roles and user-role assignments
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { createRecord, updateRecord, deleteRecord, TABLES, FIELDS } from '../lib/api-switch';
import { queryRecords } from '../api/client';
import { useAuthStore } from './authStore';
import { logActivity } from '../lib/auditLog';
import type { Role, UserRole, ModuleAccess, RbacPermissions, ModuleId, PermissionLevel } from '../types/rbac';
import { toModuleAccess, FULL_ACCESS, NO_ACCESS, emptyPermissions, ALL_MODULE_IDS } from '../types/rbac';

function getToken(): string | undefined {
  return useAuthStore.getState().token ?? undefined;
}

// ── Field shortcuts ───────────────────────────────────────────
const FR = FIELDS.roles;
const FUR = FIELDS.userRoles;

// ── Mappers ───────────────────────────────────────────────────

function mapRole(row: Record<string, unknown>): Role {
  const id = Number(row.record_id) || Number(row.id) || 0;
  let permissions: RbacPermissions;
  try {
    permissions = JSON.parse(String(row[FR.permissions] ?? '{}'));
  } catch {
    permissions = emptyPermissions();
  }
  return {
    id,
    Rid: id,
    roleName: String(row[FR.roleName] ?? ''),
    roleSlug: String(row[FR.roleSlug] ?? ''),
    description: String(row[FR.description] ?? ''),
    permissions,
    isDefault: String(row[FR.isDefault] ?? '') === 'true',
    sortOrder: Number(row[FR.sortOrder]) || 99,
  };
}

function mapUserRole(row: Record<string, unknown>): UserRole {
  const id = Number(row.record_id) || Number(row.id) || 0;
  return {
    id,
    Rid: id,
    userEmail: String(row[FUR.userEmail] ?? ''),
    userName: String(row[FUR.userName] ?? ''),
    roleId: String(row[FUR.roleId] ?? ''),
    roleName: String(row[FUR.roleName] ?? ''),
    assignedBy: String(row[FUR.assignedBy] ?? ''),
  };
}

// ── Store ────────────────────────────────────────────────────

interface RoleStore {
  roles: Role[];
  userRoles: UserRole[];
  myRole: Role | null;
  demoRoleOverride: Role | null;
  isLoaded: boolean;
  loading: boolean;
  error: string | null;

  loadRoles: () => Promise<void>;
  loadUserRoles: () => Promise<void>;
  loadMyRole: (email: string) => Promise<void>;
  setDemoRole: (slug: string | null) => void;
  getModuleAccess: (moduleId: ModuleId) => ModuleAccess;

  createRole: (data: {
    roleName: string;
    roleSlug: string;
    description: string;
    permissions: RbacPermissions;
    isDefault: boolean;
    sortOrder: number;
  }) => Promise<Role | null>;
  updateRole: (id: number, data: Partial<Role>) => Promise<void>;
  deleteRole: (id: number) => Promise<void>;

  assignRole: (userEmail: string, userName: string, roleId: string, roleName: string) => Promise<void>;
  removeAssignment: (id: number) => Promise<void>;
}

// ── Demo role presets ──────────────────────────────────────────
function makeDemoRole(slug: string): Role | null {
  const allAdmin = () => { const p = emptyPermissions(); ALL_MODULE_IDS.forEach(m => { p[m] = 'admin'; }); return p; };
  const allEditor = () => { const p = emptyPermissions(); ALL_MODULE_IDS.forEach(m => { p[m] = 'editor'; }); return p; };
  const allViewer = () => { const p = emptyPermissions(); ALL_MODULE_IDS.forEach(m => { p[m] = 'viewer'; }); return p; };
  const bizOwner = () => { const p = emptyPermissions(); p.mainstreet = 'editor'; return p; };
  const fieldWorker = () => {
    const p = emptyPermissions();
    (['rollcall', 'intake', 'shelter', 'debris', 'impact', 'volunteer'] as ModuleId[]).forEach(m => { p[m] = 'editor'; });
    (['hotwash', 'handoff', 'pace', 'geoops'] as ModuleId[]).forEach(m => { p[m] = 'viewer'; });
    return p;
  };
  const paApplicantMgr = () => { const p = emptyPermissions(); p.pagrants = 'editor'; p.workflows = 'viewer'; return p; };
  const paApplicantStaff = () => { const p = emptyPermissions(); p.pagrants = 'editor'; p.workflows = 'viewer'; return p; };
  const paApplicantViewer = () => { const p = emptyPermissions(); p.pagrants = 'viewer'; return p; };
  const handoffOnly = () => { const p = emptyPermissions(); p.handoff = 'editor'; return p; };
  const timelaborOnly = () => { const p = emptyPermissions(); p.timelabor = 'editor'; return p; };
  const presets: Record<string, { name: string; perms: () => RbacPermissions }> = {
    super_admin: { name: 'Super Admin', perms: allAdmin },
    eoc_director: { name: 'EOC Director', perms: allAdmin },
    editor: { name: 'Editor', perms: allEditor },
    viewer: { name: 'Viewer', perms: allViewer },
    biz_owner: { name: 'Business Owner', perms: bizOwner },
    field_worker: { name: 'Field Worker', perms: fieldWorker },
    pa_applicant_mgr: { name: 'PA Applicant Mgr', perms: paApplicantMgr },
    pa_applicant_staff: { name: 'PA Applicant Staff', perms: paApplicantStaff },
    pa_applicant_viewer: { name: 'PA Applicant Viewer', perms: paApplicantViewer },
    handoff_only: { name: 'Handoff Only', perms: handoffOnly },
    timelabor_only: { name: 'Time & Labor', perms: timelaborOnly },
  };
  const p = presets[slug];
  if (!p) return null;
  return { id: -1, Rid: -1, roleName: p.name, roleSlug: slug, description: 'Demo role', permissions: p.perms(), isDefault: false, sortOrder: 0 };
}

export const useRoleStore = create<RoleStore>((set, get) => ({
  roles: [],
  userRoles: [],
  myRole: null,
  demoRoleOverride: null,
  isLoaded: false,
  loading: false,
  error: null,

  // ── Load all roles ─────────────────────────────────────────

  loadRoles: async () => {
    set({ loading: true, error: null });
    try {
      const result = await queryRecords<Record<string, unknown>>(TABLES.roles, {
        sort: { field: FIELDS.roles.sortOrder, direction: 'asc' },
        limit: 200,
      });
      const roles = (result.records ?? []).map(mapRole);
      set({ roles, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load roles' });
    }
  },

  // ── Load all user-role assignments ─────────────────────────

  loadUserRoles: async () => {
    set({ loading: true, error: null });
    let lastErr: unknown;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, attempt * 1000));
        const result = await queryRecords<Record<string, unknown>>(TABLES.userRoles, { limit: 500 });
        const userRoles = (result.records ?? []).map(mapUserRole);
        set({ userRoles, loading: false });
        return;
      } catch (err) { lastErr = err; }
    }
    set({ loading: false, error: lastErr instanceof Error ? lastErr.message : 'Failed to load user roles' });
  },

  // ── Resolve the current user's role ────────────────────────

  loadMyRole: async (email: string) => {
    const { roles, userRoles, loadRoles, loadUserRoles } = get();

    // Ensure roles are loaded
    if (roles.length === 0) await loadRoles();
    // Ensure userRoles are loaded
    if (userRoles.length === 0) await loadUserRoles();

    // Re-read after potential loads
    const currentRoles = get().roles;
    const currentUserRoles = get().userRoles;

    // Find this user's assignment
    const assignment = currentUserRoles.find(
      ur => ur.userEmail.toLowerCase() === email.toLowerCase()
    );

    let resolved: Role | null = null;

    if (assignment) {
      resolved = currentRoles.find(r => String(r.id) === assignment.roleId) ?? null;
    }

    // Fallback to default role if no assignment found
    if (!resolved) {
      resolved = currentRoles.find(r => r.isDefault) ?? null;
    }

    // Last-resort fallback: if still no role and user is admin, synthesize super_admin
    if (!resolved) {
      const authUser = useAuthStore.getState().user;
      if (authUser?.isAdmin || authUser?.role === 'super_admin' || authUser?.role === 'admin') {
        resolved = makeDemoRole('super_admin');
      }
    }

    set({ myRole: resolved, isLoaded: true });
  },

  // ── Demo role override ─────────────────────────────────────

  setDemoRole: (slug: string | null) => {
    if (!slug) {
      set({ demoRoleOverride: null });
    } else {
      set({ demoRoleOverride: makeDemoRole(slug) });
    }
  },

  // ── Get module access for a given module ───────────────────

  getModuleAccess: (moduleId: ModuleId): ModuleAccess => {
    const { myRole, demoRoleOverride } = get();
    const effectiveRole = demoRoleOverride ?? myRole;

    // Super admin / EOC director bypass
    if (effectiveRole?.roleSlug === 'super_admin' || effectiveRole?.roleSlug === 'eoc_director') {
      return FULL_ACCESS;
    }
    if (!effectiveRole) {
      // No role assigned yet — grant full access to authenticated users so tiles show.
      // Individual pages enforce their own guards. Role loading is async; this prevents
      // a blank hub on first render or when the roles table is empty in V2.
      const user = useAuthStore.getState().user;
      return user ? FULL_ACCESS : NO_ACCESS;
    }

    let level: PermissionLevel = effectiveRole.permissions[moduleId] ?? 'none';
    // Fallback: timelabor inherits from pagrants if not explicitly set
    if (level === 'none' && moduleId === 'timelabor' && effectiveRole.permissions.pagrants) {
      level = effectiveRole.permissions.pagrants;
    }
    return toModuleAccess(level);
  },

  // ── Create a new role ──────────────────────────────────────

  createRole: async (data) => {
    try {
      const fields: Array<[string, string]> = [
        [FIELDS.roles.roleName, data.roleName],
        [FIELDS.roles.roleSlug, data.roleSlug],
        [FIELDS.roles.description, data.description],
        [FIELDS.roles.permissions, JSON.stringify(data.permissions)],
        [FIELDS.roles.isDefault, String(data.isDefault)],
        [FIELDS.roles.sortOrder, String(data.sortOrder)],
      ];
      const rid = await createRecord(TABLES.roles, fields, getToken());
      await get().loadRoles();
      return get().roles.find(r => r.id === rid) ?? null;
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to create role' });
      return null;
    }
  },

  // ── Update an existing role ────────────────────────────────

  updateRole: async (id, data) => {
    try {
      const fields: Array<[string, string]> = [];
      if (data.roleName !== undefined) fields.push([FIELDS.roles.roleName, data.roleName]);
      if (data.roleSlug !== undefined) fields.push([FIELDS.roles.roleSlug, data.roleSlug]);
      if (data.description !== undefined) fields.push([FIELDS.roles.description, data.description]);
      if (data.permissions !== undefined) fields.push([FIELDS.roles.permissions, JSON.stringify(data.permissions)]);
      if (data.isDefault !== undefined) fields.push([FIELDS.roles.isDefault, String(data.isDefault)]);
      if (data.sortOrder !== undefined) fields.push([FIELDS.roles.sortOrder, String(data.sortOrder)]);

      if (fields.length > 0) {
        await updateRecord(TABLES.roles, id, fields, getToken());
      }

      await get().loadRoles();

      // Re-resolve myRole in case the updated role is the current user's role
      const user = useAuthStore.getState().user;
      if (user?.email) {
        await get().loadMyRole(user.email);
      }
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to update role' });
    }
  },

  // ── Delete a role ──────────────────────────────────────────

  deleteRole: async (id) => {
    try {
      await deleteRecord(TABLES.roles, id, getToken());
      await get().loadRoles();
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to delete role' });
    }
  },

  // ── Assign a role to a user ────────────────────────────────

  assignRole: async (userEmail, userName, roleId, roleName) => {
    try {
      const { userRoles } = get();
      const existing = userRoles.find(
        ur => ur.userEmail.toLowerCase() === userEmail.toLowerCase()
      );

      const currentUser = useAuthStore.getState().user;
      const assignedBy = currentUser?.email ?? '';

      if (existing) {
        // Update existing assignment
        const fields: Array<[string, string]> = [
          [FIELDS.userRoles.roleId, roleId],
          [FIELDS.userRoles.roleName, roleName],
          [FIELDS.userRoles.assignedBy, assignedBy],
        ];
        await updateRecord(TABLES.userRoles, existing.id, fields, getToken());
      } else {
        // Create new assignment
        const fields: Array<[string, string]> = [
          [FIELDS.userRoles.userEmail, userEmail],
          [FIELDS.userRoles.userName, userName],
          [FIELDS.userRoles.roleId, roleId],
          [FIELDS.userRoles.roleName, roleName],
          [FIELDS.userRoles.assignedBy, assignedBy],
        ];
        await createRecord(TABLES.userRoles, fields, getToken());
      }

      await get().loadUserRoles();
      logActivity('settings', 'assign', 'user_role', userEmail, `Role assigned: ${roleName} to ${userName || userEmail}`);
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to assign role' });
    }
  },

  // ── Remove a role assignment ───────────────────────────────

  removeAssignment: async (id) => {
    try {
      await deleteRecord(TABLES.userRoles, id, getToken());
      await get().loadUserRoles();
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to remove assignment' });
    }
  },
}));
